import { CurrentPractitionerQuery, PractitionerDetails } from '@dreryk/egabinet-personnel-frontend-api';

import { performCqrsQueryGet } from '@dreryk/command-query-react-common';
import { useFormController } from '@dreryk/react-common';
import { StyledFlex } from '@dreryk/react-components';

import { protectFeature } from '~libs.shared.system';
import { useCurrentPractitionerConfidentialityMode } from '~libs.shared.account';

import { getPatientDisplayedFullName } from '../../../common/common.utils';
import { patientMedicalInformationTranslationKeys } from '../../../translations';
import { MedicalInformationAttachments } from '../../shared/fields/MedicalInformationAttachments';
import { MedicalInformationConfidentialityLevelField } from '../../shared/fields/MedicalInformationConfidentialityLevelField/MedicalInformationConfidentialityLevelField';
import { MedicalInformationDescriptionTextarea } from '../../shared/fields/MedicalInformationDescriptionTextarea';

import { ChronicDiseaseEntryFormBean, ChronicDiseaseEntryFormRenderer } from './chronicDiseaseEntryForm.types';
import {
   chronicDiseaseEntryFormInitializer,
   forDoctor,
   removalSectionViewVisitor,
} from './ChronicDiseaseEntryForm.utils';
import { CreationDatePicker } from './fields/CreationDatePicker';
import { CurrentPractitionerCheckbox } from './fields/CurrentPractitionerCheckbox';
import { DiseaseSelector } from './fields/DiseaseSelector';
import { PractitionerLicenceNumber } from './fields/PractitionerLicenceNumber';
import { PractitionerName } from './fields/PractitionerName';
import { RemovalDatePicker } from './fields/RemovalDatePicker';
import { RemovalReason } from './fields/RemovalReason';

const t = patientMedicalInformationTranslationKeys.chronicDisease.form.create;

type ChronicDiseaseEntryFormProps = {
   readonly medicalInformationId: number;
   readonly children: ChronicDiseaseEntryFormRenderer;
};

// eslint-disable-next-line max-lines-per-function
export const ChronicDiseaseEntryForm = (props: ChronicDiseaseEntryFormProps): JSX.Element => {
   const updatingDoctorPersonalDataAvailable = protectFeature('updatingDoctorPersonalData');
   const defaultConfidentialityLevel = useCurrentPractitionerConfidentialityMode();
   const {
      register,
      control,
      formState,
      setValue,
      watch,
      setError,
      handleSubmit,
      sync,
   } = useFormController<ChronicDiseaseEntryFormBean>(chronicDiseaseEntryFormInitializer(defaultConfidentialityLevel));
   const { diagnosedByCurrentPractitioner, diagnosisRemovalDate } = watch();

   const handleSelectionChange = async (selection: boolean): Promise<void> => {
      if (selection) {
         const { firstName, lastName, medicalLicenceNumber } = await performCqrsQueryGet<PractitionerDetails>(
            () => new CurrentPractitionerQuery()
         );

         setValue('diagnosedByName', getPatientDisplayedFullName(firstName, lastName)!);
         setValue('diagnosedByLicenceNumber', medicalLicenceNumber!);

         setError('diagnosedByName', false);
         setError('diagnosedByLicenceNumber', false);
      }
   };

   return (
      <StyledFlex direction={'column'} gap={'24px'}>
         <StyledFlex gap={24}>
            <CreationDatePicker register={register} watch={watch} />
            <RemovalDatePicker register={register} watch={watch} />
         </StyledFlex>
         <StyledFlex gap={8} direction={'column'}>
            <StyledFlex gap={24}>
               <PractitionerName
                  register={register}
                  name={'diagnosedByName'}
                  label={t.diagnosisCreationBy.name.label}
                  placeholder={t.diagnosisCreationBy.name.placeholder}
                  disabled={diagnosedByCurrentPractitioner}
               />
               <PractitionerLicenceNumber
                  register={register}
                  name={'diagnosedByLicenceNumber'}
                  label={t.diagnosisCreationBy.licenceNumber.label}
                  disabled={diagnosedByCurrentPractitioner}
               />
            </StyledFlex>
            {forDoctor(updatingDoctorPersonalDataAvailable) && (
               <CurrentPractitionerCheckbox
                  handleSelectionChange={handleSelectionChange}
                  register={register}
                  name='diagnosedByCurrentPractitioner'
                  label={t.currentPractitioner.label}
               />
            )}
         </StyledFlex>
         {removalSectionViewVisitor(
            updatingDoctorPersonalDataAvailable,
            diagnosisRemovalDate
         )({
            forDoctor: () => <RemovalReason register={register} />,
            forSpecialist: () => (
               <>
                  <RemovalReason register={register} />
                  <StyledFlex gap={8} direction={'column'}>
                     <StyledFlex gap={24}>
                        <PractitionerName
                           register={register}
                           name={'removalDiagnosisByName'}
                           label={t.diagnosisRemovalBy.name.label}
                           placeholder={t.diagnosisRemovalBy.name.placeholder}
                        />
                        <PractitionerLicenceNumber
                           register={register}
                           name={'removalDiagnosisByLicenceNumber'}
                           label={t.diagnosisRemovalBy.licenceNumber.label}
                        />
                     </StyledFlex>
                  </StyledFlex>
               </>
            ),
         })}
         <DiseaseSelector register={register} />
         <MedicalInformationDescriptionTextarea register={register} control={control} />
         <MedicalInformationConfidentialityLevelField register={register} />
         <MedicalInformationAttachments
            controller={register('attachments')}
            medicalInformationId={props.medicalInformationId}
            sync={sync}
         />

         {props.children({ formState: formState, handleSubmit: handleSubmit })}
      </StyledFlex>
   );
};
